import React, {
  memo,
  useCallback, useContext, useMemo, useState,
} from 'react';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faKey, faUserCheck, faTimes, faBolt,
} from '@fortawesome/free-solid-svg-icons';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField,
} from '@mui/material';
import { Alert } from '@mui/lab';
import api from '../../../api/req';
import { AppContext } from '../../../providers/authProvider';

const PASSWORD_CHARS = '0123456789abcdefghijklmnopqrstuvwxyz_$!ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const PASSWORD_LENGTH = 8;

function SetPasswordButton({
  userid,
  disabled,
  onSuccess,
  email,
}) {
  const [visible, setVisible] = useState(false);
  const [passwords, setPasswords] = useState({
    one: '',
    two: '',
  });
  const [message, setMessage] = useState(null);
  const [err, setErr] = useState(null);
  const { auth } = useContext(AppContext);
  const setPassword = useCallback(
    async () => {
      const data = {
        new_password: passwords.one,
        confirm_password: passwords.two,
      };
      const r = await api.post(`/api/core/user/${userid}/set_password/`, auth, data);
      if (!r.ok) throw new Error(`Виникла помилка! Перевірте правильність заповнення. (${r.status} ${r.statusText})`);
      return true;
    },
    [auth, passwords.one, passwords.two, userid],
  );

  const onSetPassword = useCallback(
    () => {
      setMessage(null);
      setErr(null);
      setPassword()
        .then(() => {
          setPasswords((oldPasswords) => ({
            ...oldPasswords,
            one: '',
            two: '',
          }));
          setMessage('Пароль встановлений!');
          if (onSuccess) onSuccess();
        })
        .catch((e) => setErr(e.message));
    },
    [onSuccess, setPassword],
  );

  const twoMessage = useMemo(
    () => (passwords.one !== passwords.two ? 'Паролі не співпадають' : null),
    [passwords.one, passwords.two],
  );

  const onGeneratePassword = useCallback(
    () => {
      const newPassword = [...Array(PASSWORD_LENGTH)].reduce((R) => {
        const r = Math.floor(Math.random() * PASSWORD_CHARS.length);
        return R + PASSWORD_CHARS.substring(r, r + 1);
      }, '');
      setPasswords({ one: newPassword, two: newPassword });
      navigator.clipboard.writeText(newPassword);
      setMessage(`Новий пароль: ${newPassword} встановлено та скопійовано до буферу обміну`);
    },
    [],
  );

  return (
    <div>
      <NavLink to={`/resetPassword/${email}`}>
        <Button
        // onClick={() => setVisible(true)}
          disabled={disabled}
          startIcon={<FontAwesomeIcon icon={faKey} />}
          variant="contained"
        >
          Встановити пароль
        </Button>
      </NavLink>
      <Dialog open={visible} onClose={() => setVisible(false)} maxWidth="md" fullWidth>
        <DialogTitle>Встановлення паролю</DialogTitle>
        <DialogContent>

          <Grid container spacing={2}>
            <Grid item sm={6}>
              <TextField
                autoFocus
                margin="dense"
                label="Пароль"
                type="password"
                autoComplete="new-password"
                helperText={message}
                fullWidth
                onChange={(e) => setPasswords((o) => ({ ...o, one: e.target.value }))}
              />
            </Grid>
            <Grid item sm={6}>
              <TextField
                autoFocus
                margin="dense"
                label="Пароль ще раз"
                type="password"
                autoComplete="new-password"
                error={!!twoMessage}
                helperText={twoMessage}
                fullWidth
                onChange={(e) => setPasswords((o) => ({ ...o, two: e.target.value }))}
              />
            </Grid>
          </Grid>
          {err && (
            <Alert severity="error">
              {err}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onGeneratePassword} color="success" startIcon={<FontAwesomeIcon icon={faBolt} />}>
            Згенерувати гарний пароль
          </Button>
          <Button onClick={onSetPassword} startIcon={<FontAwesomeIcon icon={faUserCheck} />}>
            Встановити
          </Button>
          <Button onClick={() => setVisible(false)} startIcon={<FontAwesomeIcon icon={faTimes} color="secondary" />}>
            Закрити
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
SetPasswordButton.propTypes = {
  userid: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  email: PropTypes.string,
  onSuccess: PropTypes.func,
};

SetPasswordButton.defaultProps = {
  disabled: false,
  onSuccess: null,
  email: '',
};

export default memo(SetPasswordButton);
